import type { TypeWindow } from 'geoview-core/core/types/global-types';
import { logger } from 'geoview-core/core/utils/logger';
import { TYPOGRAPHY_SCALE } from 'geoview-core/ui/style/default';

import { DescriptionText } from './description-text';
import type { TypeHeaderLayer } from '../custom-legend-types';
import { isHeaderLayer } from '../custom-legend-types';
import type { getSxClasses } from '../custom-legend-style';

/** Props for the HeaderItem component. */
interface HeaderItemProps {
  item: TypeHeaderLayer;
  sxClasses: ReturnType<typeof getSxClasses>;
}

/**
 * Renders a header item.
 *
 * @param props - Component props
 * @returns The rendered header, or undefined if the item is not a header
 */
export function HeaderItem({ item, sxClasses }: HeaderItemProps): JSX.Element | undefined {
  logger.logTraceRender('geoview-custom-legend/components/header-item');

  const { cgpv } = window as TypeWindow;
  const { ui } = cgpv;
  const { Box, Typography } = ui.elements;
  const theme = ui.useTheme();

  if (!isHeaderLayer(item)) return;

  // Create the override object and if values are present, they will override the default values
  let fontSize: string | undefined;
  if (item.fontSizeStep !== undefined) {
    const size = TYPOGRAPHY_SCALE[item.fontSizeStep];
    if (typeof size === 'number') fontSize = theme.typography.pxToRem(size);
    else logger.logWarning(`Custom legend: unknown fontSizeStep "${item.fontSizeStep}"; keeping the default header size.`);
  } else if (item.fontSize !== undefined) fontSize = theme.typography.pxToRem(item.fontSize);
  const styleOverrides = {
    ...(fontSize !== undefined && { fontSize }),
    ...(item.fontWeight && { fontWeight: item.fontWeight }),
  };

  return (
    <Box sx={sxClasses.headerItem}>
      <Typography component="h3" sx={{ ...sxClasses.headerText, ...styleOverrides }}>
        {item.text}
      </Typography>
      {item.description && <DescriptionText description={item.description} sxClasses={sxClasses} />}
    </Box>
  );
}
